/**
 * vozidlo-section-forms — dumb form-body components for the vehicle *card*
 * sections (Identifikace · Specifikace · Cenotvorba · Faktura · Stav ·
 * Poznámka · Výbava). One implementation per detail-page section, shared by
 * the edit-modal wrappers (`VozidloEditModals`) and the stacked create /
 * create-continuation surface (`VozidloForm`). The existing `section-forms`
 * stays offer/contract-specific — the card groups its fields differently
 * (per what the detail page displays), so the bodies live here.
 *
 * Each body owns its fields, local state, seeding, and payload mapping, and
 * renders `<form id={…_FORM_ID}>` with fields only — the chrome (Modal.Foot /
 * VozidloForm's Savebar) supplies the submit button via the HTML `form`
 * attribute. Seeding is dirty-guarded: bodies re-seed from fresh `initial`
 * data until the user starts editing; modals remount their content per open
 * (Radix unmounts the closed dialog), so "re-seed on open" keeps working.
 *
 * Two consumption modes:
 *   - modals: `onSubmit` receives the section's slice value; the wrapper maps
 *     it to a slice-only `VehicleWriteBody` via the `…ToBody` helpers below
 *     and PUTs just that subset (the endpoint accepts any subset).
 *   - stacked form: `onChange` mirrors every seed/edit up so `VozidloForm`
 *     can assemble the one full-body create/update payload; the per-section
 *     `<form>`s are never submitted there (no button targets them).
 *
 * Dual-VAT price entry (Cenotvorba) keeps the VozidloForm semantics exactly:
 * the s-DPH/bez-DPH counterparts are UI-only mirrors recomputed via the
 * standard 21 % rate (`STANDARD_VAT_ID`) and are never persisted — persisted
 * values stay prodejní s DPH, ceníková + nákupní bez DPH.
 */

import { statusOptions } from '@/lib/stock-status';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Field, Fields } from '@/components/Field';
import { Icon } from '@/components/Icon';
import { SelectInput } from '@/components/SelectInput';
import { TextInput } from '@/components/TextInput';
import { EquipmentPriceFields, bezFromSDph, sDphFromBez } from './EquipmentPriceFields';
import { formatDate } from '@/lib/format';
import { dateInputValue } from './section-forms';
import { useVinSkupina } from '@/modules/skupiny/api';
import { vinSkupinaWarning } from '@/modules/skupiny/helpers';
import {
  useBrandsQuery,
  useVatRatesQuery,
  useVehicleModelsQuery,
  type VatRate,
  type VehicleDetail,
  type VehicleEquipmentInput,
  type VehicleWriteBody,
} from './api';

// Vehicle-level prices have no per-row DPH select — they always use the
// standard rate (id_dph 1, 21 %), same as the detail page and list previews.
export const STANDARD_VAT_ID = 1;

// Canonical status set the backend writes — keep in sync with the new-vehicle sales-contract close
// (vozidla-smlouvy.repository.ts sets 'Zákaznický'). A value not in this list
// makes the <select> silently fall back to the first option.
export const VEHICLE_STATUSES = [
  'Skladový',
  'Zákaznický',
  'Registrovaný',
  'Předaný dealerovi',
  'Předaný zákazníkovi',
];

/**
 * Shared body state: local form seeded from `initial`, dirty-gated re-seed
 * (fresh fetches replace the seed until the user edits — see the
 * `section-forms` equipment comment for the failure mode a one-time seed
 * causes), and an `onChange` mirror invoked on seed and on every edit so the
 * stacked form's merged copy never goes stale.
 */
export function useSeededSlice<T extends object>(initial: T, onChange?: (value: T) => void) {
  const [form, setForm] = useState<T>(initial);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (dirty) return;
    setForm(initial);
    onChange?.(initial);
    // `onChange` is a mirror callback — the seed input is the dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, dirty]);

  const set = <K extends keyof T>(k: K, v: T[K]) => {
    const next = { ...form, [k]: v } as T;
    setForm(next);
    setDirty(true);
    onChange?.(next);
  };

  const patch = (p: Partial<T>) => {
    const next = { ...form, ...p } as T;
    setForm(next);
    setDirty(true);
    onChange?.(next);
  };

  return { form, set, patch };
}

export interface BodyProps<T> {
  /** Fresh seed (detail query / create defaults) — re-seeds until the user edits. */
  initial: T;
  /** True while the wrapper's mutation is in flight — blocks submit. */
  busy: boolean;
  /** Modal mode: the section's slice value on form submit. */
  onSubmit?: (form: T) => void;
  /** Stacked-form mode: mirrors seed + every edit up to the composer. */
  onChange?: (form: T) => void;
}

// ─── Identifikace ───────────────────────────────────────────────────────────

export const VOZIDLO_IDENTIFIKACE_FORM_ID = 'vozidlo-identifikace-form';

export interface VozidloIdentifikaceValue {
  vc_auta: string;
  spz_vozu: string;
  znacka_vozu: number;
  vozidlo_model: number;
  modelovy_rok: string;
  provedeni: string;
  cislo_skladove_karty: string;
}

export function identifikaceSeed(
  v?: VehicleDetail | null,
  presetBrand = 0,
): VozidloIdentifikaceValue {
  return {
    vc_auta: v?.vc_auta ?? '',
    spz_vozu: v?.spz_vozu ?? '',
    znacka_vozu: v ? Number(v.znacka_vozu ?? 0) : presetBrand,
    vozidlo_model: Number(v?.vozidlo_model ?? 0),
    modelovy_rok: v?.modelovy_rok ?? '',
    provedeni: v?.provedeni ?? '',
    cislo_skladove_karty: v?.cislo_skladove_karty ?? '',
  };
}

export function identifikaceToBody(s: VozidloIdentifikaceValue): VehicleWriteBody {
  return {
    vc_auta: s.vc_auta,
    spz_vozu: s.spz_vozu,
    znacka_vozu: s.znacka_vozu || undefined,
    vozidlo_model: s.vozidlo_model || undefined,
    modelovy_rok: s.modelovy_rok,
    provedeni: s.provedeni,
    cislo_skladove_karty: s.cislo_skladove_karty,
  };
}

export function VozidloIdentifikaceFormBody({
  initial,
  busy,
  onSubmit,
  onChange,
}: BodyProps<VozidloIdentifikaceValue>) {
  const { form, set, patch } = useSeededSlice(initial, onChange);
  const brands = useBrandsQuery();
  const models = useVehicleModelsQuery(form.znacka_vozu || null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };

  return (
    <form id={VOZIDLO_IDENTIFIKACE_FORM_ID} onSubmit={submit}>
      <Fields cols={3}>
        <Field label="Značka">
          <SelectInput
            value={String(form.znacka_vozu)}
            onChange={(e) =>
              // Brand swap invalidates the model — clear it so the user re-picks.
              patch({
                znacka_vozu: Number(e.currentTarget.value) || 0,
                vozidlo_model: 0,
              })
            }
          >
            <option value="0">— Vyberte —</option>
            {(brands.data?.brands ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.nazev}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Model">
          <SelectInput
            value={String(form.vozidlo_model)}
            onChange={(e) => set('vozidlo_model', Number(e.currentTarget.value) || 0)}
            disabled={!form.znacka_vozu}
          >
            <option value="0">
              {form.znacka_vozu ? '— Vyberte —' : 'Nejprve značka'}
            </option>
            {(models.data?.models ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.nazev_modelu}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Modelový rok">
          <TextInput
            value={form.modelovy_rok}
            onChange={(e) => set('modelovy_rok', e.currentTarget.value)}
            placeholder="2026"
          />
        </Field>
        <Field label="Provedení">
          <TextInput
            value={form.provedeni}
            onChange={(e) => set('provedeni', e.currentTarget.value)}
            placeholder="Trend"
          />
        </Field>
        <Field label="SPZ">
          <TextInput
            value={form.spz_vozu}
            onChange={(e) => set('spz_vozu', e.currentTarget.value)}
            placeholder="1AB 2345"
          />
        </Field>
        <Field label="Č. objednávky">
          <TextInput
            value={form.vc_auta}
            onChange={(e) => set('vc_auta', e.currentTarget.value)}
          />
        </Field>
        <Field label="Č. skladové karty">
          <TextInput
            value={form.cislo_skladove_karty}
            onChange={(e) => set('cislo_skladove_karty', e.currentTarget.value)}
          />
        </Field>
      </Fields>
    </form>
  );
}

// ─── Specifikace ────────────────────────────────────────────────────────────

export const VOZIDLO_SPECIFIKACE_FORM_ID = 'vozidlo-specifikace-form';

export interface VozidloSpecifikaceValue {
  motor: string;
  barva: string;
  prevodovka: string;
  calouneni: string;
  vin: string;
  verze: string;
}

export function specifikaceSeed(v?: VehicleDetail | null): VozidloSpecifikaceValue {
  return {
    motor: v?.motor ?? '',
    barva: v?.barva ?? '',
    prevodovka: v?.prevodovka ?? '',
    calouneni: v?.calouneni ?? '',
    vin: v?.vin ?? '',
    verze: v?.verze ?? '',
  };
}

export function specifikaceToBody(s: VozidloSpecifikaceValue): VehicleWriteBody {
  return {
    motor: s.motor,
    barva: s.barva,
    prevodovka: s.prevodovka,
    calouneni: s.calouneni,
    vin: s.vin,
    verze: s.verze,
  };
}

export function VozidloSpecifikaceFormBody({
  initial,
  busy,
  onSubmit,
  onChange,
  vozidloId,
}: BodyProps<VozidloSpecifikaceValue> & {
  /** The saved vehicle being edited (omit on create) — keeps the VIN check from flagging its own transfer target. */
  vozidloId?: number | null;
}) {
  const { form, set } = useSeededSlice(initial, onChange);
  const vinHolders = useVinSkupina(form.vin, { kind: 'nove', id: vozidloId, saved: initial.vin });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };

  return (
    <form id={VOZIDLO_SPECIFIKACE_FORM_ID} onSubmit={submit}>
      <Fields cols={3}>
        <Field label="Motor">
          <TextInput value={form.motor} onChange={(e) => set('motor', e.currentTarget.value)} />
        </Field>
        <Field label="Převodovka">
          <TextInput
            value={form.prevodovka}
            onChange={(e) => set('prevodovka', e.currentTarget.value)}
          />
        </Field>
        <Field label="Verze">
          <TextInput value={form.verze} onChange={(e) => set('verze', e.currentTarget.value)} />
        </Field>
        <Field label="Barva">
          <TextInput value={form.barva} onChange={(e) => set('barva', e.currentTarget.value)} />
        </Field>
        <Field label="Čalounění">
          <TextInput
            value={form.calouneni}
            onChange={(e) => set('calouneni', e.currentTarget.value)}
          />
        </Field>
        <Field label="VIN" hint={vinSkupinaWarning(vinHolders) ?? undefined} hintTone="warn">
          <TextInput
            value={form.vin}
            onChange={(e) => set('vin', e.currentTarget.value)}
            placeholder="WBA…"
          />
        </Field>
      </Fields>
    </form>
  );
}

// ─── Cenotvorba ─────────────────────────────────────────────────────────────

export const VOZIDLO_CENOTVORBA_FORM_ID = 'vozidlo-cenotvorba-form';

export interface VozidloCenotvorbaValue {
  prodejni_cena_navrh: string;
  cenikova_cena: string;
  nakupni_cena: string;
  fleet_zaloha: number;
  fleet_predpoklad: number;
  fleet_skutecnost: number;
  marketing_predpoklad: number;
}

/** Internal state = persisted values + the UI-only dual-VAT mirrors. */
interface CenotvorbaDraft extends VozidloCenotvorbaValue {
  /**
   * UI-only mirrors for dual VAT entry (same pattern as the equipment rows'
   * `cenaSDph`) — never part of the submitted slice. Persisted values stay as
   * before: prodejní s DPH, ceníková + nákupní bez DPH.
   */
  prodejniCenaBezDph: string;
  cenikovaCenaSDph: string;
  nakupniCenaSDph: string;
}

export function cenotvorbaSeed(v?: VehicleDetail | null): VozidloCenotvorbaValue {
  return {
    prodejni_cena_navrh: v?.prodejni_cena_navrh ?? '',
    cenikova_cena: v?.cenikova_cena ?? '',
    nakupni_cena: v?.nakupni_cena ?? '',
    fleet_zaloha: Number(v?.fleet_zaloha ?? 0),
    fleet_predpoklad: Number(v?.fleet_predpoklad ?? 0),
    fleet_skutecnost: Number(v?.fleet_skutecnost ?? 0),
    marketing_predpoklad: Number(v?.marketing_predpoklad ?? 0),
  };
}

export function cenotvorbaToBody(s: VozidloCenotvorbaValue): VehicleWriteBody {
  return {
    prodejni_cena_navrh: s.prodejni_cena_navrh,
    cenikova_cena: s.cenikova_cena,
    nakupni_cena: s.nakupni_cena,
    fleet_zaloha: s.fleet_zaloha || 0,
    fleet_predpoklad: s.fleet_predpoklad || 0,
    fleet_skutecnost: s.fleet_skutecnost || 0,
    marketing_predpoklad: s.marketing_predpoklad || 0,
  };
}

/** Strip the UI-only mirrors before handing the slice out. */
function cenotvorbaValue(d: CenotvorbaDraft): VozidloCenotvorbaValue {
  return {
    prodejni_cena_navrh: d.prodejni_cena_navrh,
    cenikova_cena: d.cenikova_cena,
    nakupni_cena: d.nakupni_cena,
    fleet_zaloha: d.fleet_zaloha,
    fleet_predpoklad: d.fleet_predpoklad,
    fleet_skutecnost: d.fleet_skutecnost,
    marketing_predpoklad: d.marketing_predpoklad,
  };
}

export function VozidloCenotvorbaFormBody({
  initial,
  busy,
  onSubmit,
  onChange,
}: BodyProps<VozidloCenotvorbaValue>) {
  const vatRates = useVatRatesQuery();
  const rates = vatRates.data?.rates ?? [];

  const [form, setForm] = useState<CenotvorbaDraft>({
    ...initial,
    prodejniCenaBezDph: '',
    cenikovaCenaSDph: '',
    nakupniCenaSDph: '',
  });
  const [dirty, setDirty] = useState(false);

  // Re-seed from fresh data until the user starts editing. Rates land async
  // and only affect the derived mirrors, so re-seeding when they arrive is
  // correct (the persisted values don't move).
  useEffect(() => {
    if (dirty) return;
    const seedRates = vatRates.data?.rates ?? [];
    setForm({
      ...initial,
      prodejniCenaBezDph: bezFromSDph(initial.prodejni_cena_navrh, STANDARD_VAT_ID, seedRates),
      cenikovaCenaSDph: sDphFromBez(initial.cenikova_cena, STANDARD_VAT_ID, seedRates),
      nakupniCenaSDph: sDphFromBez(initial.nakupni_cena, STANDARD_VAT_ID, seedRates),
    });
    onChange?.(initial);
    // `onChange` is a mirror callback — the seed inputs are the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, dirty, vatRates.data]);

  // Dual-VAT price entry: editing either side of a pair recomputes the
  // counterpart, so both keys land in one state update.
  const setPricePair = (patch: Partial<CenotvorbaDraft>) => {
    const next = { ...form, ...patch };
    setForm(next);
    setDirty(true);
    onChange?.(cenotvorbaValue(next));
  };

  const set = <K extends keyof CenotvorbaDraft>(k: K, v: CenotvorbaDraft[K]) =>
    setPricePair({ [k]: v } as Partial<CenotvorbaDraft>);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(cenotvorbaValue(form));
  };

  return (
    <form id={VOZIDLO_CENOTVORBA_FORM_ID} onSubmit={submit}>
      <Fields cols={3}>
        {/* Row 1: bez DPH · Row 2: s DPH — each column is one price. */}
        <Field label="Prodejní cena — návrh (bez DPH)">
          <TextInput
            type="number"
            value={form.prodejniCenaBezDph}
            onChange={(e) =>
              setPricePair({
                prodejniCenaBezDph: e.currentTarget.value,
                prodejni_cena_navrh: sDphFromBez(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
        <Field label="Ceníková cena (bez DPH)">
          <TextInput
            type="number"
            value={form.cenikova_cena}
            onChange={(e) =>
              setPricePair({
                cenikova_cena: e.currentTarget.value,
                cenikovaCenaSDph: sDphFromBez(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
        <Field label="Nákupní cena (bez DPH)">
          <TextInput
            type="number"
            value={form.nakupni_cena}
            onChange={(e) =>
              setPricePair({
                nakupni_cena: e.currentTarget.value,
                nakupniCenaSDph: sDphFromBez(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
        <Field label="Prodejní cena — návrh (s DPH)">
          <TextInput
            type="number"
            value={form.prodejni_cena_navrh}
            onChange={(e) =>
              setPricePair({
                prodejni_cena_navrh: e.currentTarget.value,
                prodejniCenaBezDph: bezFromSDph(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
        <Field label="Ceníková cena (s DPH)">
          <TextInput
            type="number"
            value={form.cenikovaCenaSDph}
            onChange={(e) =>
              setPricePair({
                cenikovaCenaSDph: e.currentTarget.value,
                cenikova_cena: bezFromSDph(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
        <Field label="Nákupní cena (s DPH)">
          <TextInput
            type="number"
            value={form.nakupniCenaSDph}
            onChange={(e) =>
              setPricePair({
                nakupniCenaSDph: e.currentTarget.value,
                nakupni_cena: bezFromSDph(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
        <Field label="Fleet záloha">
          <TextInput
            type="number"
            value={String(form.fleet_zaloha || 0)}
            onChange={(e) => set('fleet_zaloha', Number(e.currentTarget.value) || 0)}
            suffix="Kč"
          />
        </Field>
        <Field label="Fleet předpoklad">
          <TextInput
            type="number"
            value={String(form.fleet_predpoklad || 0)}
            onChange={(e) => set('fleet_predpoklad', Number(e.currentTarget.value) || 0)}
            suffix="%"
          />
        </Field>
        <Field label="Fleet skutečnost">
          <TextInput
            type="number"
            value={String(form.fleet_skutecnost || 0)}
            onChange={(e) => set('fleet_skutecnost', Number(e.currentTarget.value) || 0)}
            suffix="Kč"
          />
        </Field>
        <Field label="Marketing předpoklad">
          <TextInput
            type="number"
            value={String(form.marketing_predpoklad || 0)}
            onChange={(e) => set('marketing_predpoklad', Number(e.currentTarget.value) || 0)}
            suffix="Kč"
          />
        </Field>
      </Fields>
    </form>
  );
}

// ─── Faktura ────────────────────────────────────────────────────────────────

export const VOZIDLO_FAKTURA_FORM_ID = 'vozidlo-faktura-form';

export interface VozidloFakturaValue {
  cislo_faktury: number;
  datum_faktury: string;
  hodnota_faktury: string;
  umisteni: string;
}

export function fakturaSeed(v?: VehicleDetail | null): VozidloFakturaValue {
  return {
    cislo_faktury: Number(v?.cislo_faktury ?? 0),
    datum_faktury: dateInputValue(v?.datum_faktury),
    hodnota_faktury: v?.hodnota_faktury ?? '',
    umisteni: v?.umisteni ?? '',
  };
}

export function fakturaToBody(s: VozidloFakturaValue): VehicleWriteBody {
  return {
    cislo_faktury: s.cislo_faktury || 0,
    datum_faktury: s.datum_faktury,
    hodnota_faktury: s.hodnota_faktury,
    umisteni: s.umisteni,
  };
}

/** UI-only s-DPH mirror of `hodnota_faktury` (bez DPH is what persists). */
interface FakturaDraft extends VozidloFakturaValue {
  hodnotaFakturySDph: string;
}

/** Strip the UI-only mirror before handing the slice out. */
function fakturaValue(d: FakturaDraft): VozidloFakturaValue {
  return {
    cislo_faktury: d.cislo_faktury,
    datum_faktury: d.datum_faktury,
    hodnota_faktury: d.hodnota_faktury,
    umisteni: d.umisteni,
  };
}

export function VozidloFakturaFormBody({
  initial,
  busy,
  onSubmit,
  onChange,
}: BodyProps<VozidloFakturaValue>) {
  const vatRates = useVatRatesQuery();
  const rates = vatRates.data?.rates ?? [];

  const [form, setForm] = useState<FakturaDraft>({ ...initial, hodnotaFakturySDph: '' });
  const [dirty, setDirty] = useState(false);

  // Re-seed from fresh data until the user starts editing; rates land async
  // and only affect the derived mirror (see the Cenotvorba body).
  useEffect(() => {
    if (dirty) return;
    const seedRates = vatRates.data?.rates ?? [];
    setForm({
      ...initial,
      hodnotaFakturySDph: sDphFromBez(initial.hodnota_faktury, STANDARD_VAT_ID, seedRates),
    });
    onChange?.(initial);
    // `onChange` is a mirror callback — the seed inputs are the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, dirty, vatRates.data]);

  const setDraft = (patch: Partial<FakturaDraft>) => {
    const next = { ...form, ...patch };
    setForm(next);
    setDirty(true);
    onChange?.(fakturaValue(next));
  };

  const set = <K extends keyof FakturaDraft>(k: K, v: FakturaDraft[K]) =>
    setDraft({ [k]: v } as Partial<FakturaDraft>);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(fakturaValue(form));
  };

  return (
    <form id={VOZIDLO_FAKTURA_FORM_ID} onSubmit={submit}>
      <Fields cols={3}>
        <Field label="Č. faktury">
          <TextInput
            type="number"
            value={String(form.cislo_faktury || 0)}
            onChange={(e) => set('cislo_faktury', Number(e.currentTarget.value) || 0)}
          />
        </Field>
        <Field label="Datum faktury">
          <TextInput
            type="date"
            value={form.datum_faktury}
            onChange={(e) => set('datum_faktury', e.currentTarget.value)}
          />
        </Field>
        <Field label="Umístění">
          <TextInput
            value={form.umisteni}
            onChange={(e) => set('umisteni', e.currentTarget.value)}
          />
        </Field>
        <Field label="Hodnota faktury (bez DPH)">
          <TextInput
            type="number"
            value={form.hodnota_faktury}
            onChange={(e) =>
              setDraft({
                hodnota_faktury: e.currentTarget.value,
                hodnotaFakturySDph: sDphFromBez(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
        <Field label="Hodnota faktury (s DPH)">
          <TextInput
            type="number"
            value={form.hodnotaFakturySDph}
            onChange={(e) =>
              setDraft({
                hodnotaFakturySDph: e.currentTarget.value,
                hodnota_faktury: bezFromSDph(e.currentTarget.value, STANDARD_VAT_ID, rates),
              })
            }
            suffix="Kč"
          />
        </Field>
      </Fields>
    </form>
  );
}

// ─── Stav & dispozice ───────────────────────────────────────────────────────

export const VOZIDLO_STAV_FORM_ID = 'vozidlo-stav-form';

export interface VozidloStavValue {
  stav_vozidla: string;
  datum_predani: string;
  rezervace: string;
  /** Detail-page grouping wins: displayed under Stav, so edited here too. */
  vyplaceno_fmc: string;
  /** Tracked deadlines — moved by confirming a servis úkon of the car. */
  prvni_registrace: string;
  stk_platnost: string;
  serviska: string;
}

/** First STK of a new car: 4 years after the first registration — only while that is still ahead. */
export function firstStkSuggestion(prvniRegistrace: string, today: Date = new Date()): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(prvniRegistrace);
  if (!m) return null;
  const d = new Date(Number(m[1]) + 4, Number(m[2]) - 1, Number(m[3]));
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (Number.isNaN(d.getTime()) || d <= t) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function stavSeed(v?: VehicleDetail | null): VozidloStavValue {
  return {
    stav_vozidla: v?.stav_vozidla ?? 'Skladový',
    datum_predani: dateInputValue(v?.datum_predani),
    rezervace: v?.rezervace ?? '',
    vyplaceno_fmc: v?.vyplaceno_fmc ?? '',
    prvni_registrace: dateInputValue(v?.prvni_registrace),
    stk_platnost: dateInputValue(v?.stk_platnost),
    serviska: dateInputValue(v?.serviska),
  };
}

export function stavToBody(s: VozidloStavValue): VehicleWriteBody {
  return {
    stav_vozidla: s.stav_vozidla,
    datum_predani: s.datum_predani,
    rezervace: s.rezervace,
    vyplaceno_fmc: s.vyplaceno_fmc,
    prvni_registrace: s.prvni_registrace,
    stk_platnost: s.stk_platnost,
    serviska: s.serviska,
  };
}

export function VozidloStavFormBody({
  initial,
  busy,
  onSubmit,
  onChange,
}: BodyProps<VozidloStavValue>) {
  const { form, set } = useSeededSlice(initial, onChange);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };

  return (
    <form id={VOZIDLO_STAV_FORM_ID} onSubmit={submit}>
      <Fields cols={3}>
        <Field label="Stav vozidla">
          <SelectInput
            value={form.stav_vozidla}
            // A transferred row's state is final (only a transfer sets or changes it).
            disabled={form.stav_vozidla === 'Převedeno'}
            onChange={(e) => set('stav_vozidla', e.currentTarget.value)}
          >
            {statusOptions(VEHICLE_STATUSES, form.stav_vozidla).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Datum předání">
          <TextInput
            type="date"
            value={form.datum_predani}
            onChange={(e) => set('datum_predani', e.currentTarget.value)}
          />
        </Field>
        <Field label="Rezervace">
          <TextInput
            value={form.rezervace}
            onChange={(e) => set('rezervace', e.currentTarget.value)}
            placeholder="Jméno klienta nebo poznámka"
          />
        </Field>
        <Field label="Vyplaceno (FMC)">
          <TextInput
            value={form.vyplaceno_fmc}
            onChange={(e) => set('vyplaceno_fmc', e.currentTarget.value)}
          />
        </Field>
        <Field label="První registrace" optional="volitelné">
          <TextInput
            type="date"
            value={form.prvni_registrace}
            onChange={(e) => set('prvni_registrace', e.currentTarget.value)}
          />
        </Field>
        <Field label="Platnost STK" optional="volitelné">
          <TextInput
            type="date"
            value={form.stk_platnost}
            onChange={(e) => set('stk_platnost', e.currentTarget.value)}
          />
        </Field>
        <Field label="Servisní prohlídka do" optional="volitelné">
          <TextInput
            type="date"
            value={form.serviska}
            onChange={(e) => set('serviska', e.currentTarget.value)}
          />
        </Field>
      </Fields>
      {(() => {
        const first = !form.stk_platnost ? firstStkSuggestion(form.prvni_registrace) : null;
        return first ? (
          <p className="mt-2 text-[12px] text-ink-3" data-testid="prvni-stk-navrh">
            První STK je 4 roky po první registraci ({formatDate(first)}).{' '}
            <button
              type="button"
              className="text-accent-text hover:underline"
              onClick={() => set('stk_platnost', first)}
            >
              Použít
            </button>
          </p>
        ) : null;
      })()}
    </form>
  );
}

// ─── Poznámka ───────────────────────────────────────────────────────────────

export const VOZIDLO_POZNAMKA_FORM_ID = 'vozidlo-poznamka-form';

export interface VozidloPoznamkaValue {
  poznamka: string;
}

export function poznamkaSeed(v?: VehicleDetail | null): VozidloPoznamkaValue {
  return { poznamka: v?.poznamka ?? '' };
}

export function VozidloPoznamkaFormBody({
  initial,
  busy,
  onSubmit,
  onChange,
}: BodyProps<VozidloPoznamkaValue>) {
  const { form, set } = useSeededSlice(initial, onChange);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };

  return (
    <form id={VOZIDLO_POZNAMKA_FORM_ID} onSubmit={submit}>
      <textarea
        className="min-h-[140px] w-full resize-y border border-line bg-paper px-3 py-2.5 font-mono text-[12px] leading-[1.6] tracking-[-0.01em] text-ink outline-none focus:border-ink"
        aria-label="Interní poznámka"
        value={form.poznamka}
        onChange={(e) => set('poznamka', e.currentTarget.value)}
      />
    </form>
  );
}

// ─── Výbava (flat vozidlo_vybava list) ──────────────────────────────────────
//
// The card's equipment is a single flat list (code + name + price + VAT) —
// NOT the offer/contract original/neoriginal split (`EquipmentFormBody`).
// The save endpoint replaces the whole set atomically, so blank-name rows are
// dropped and removal means deletion. The hydration guard stays chrome-side:
// consumers mount this body only once the equipment fetch has data — saving
// during the initial fetch (or after it failed) would submit an empty list
// and wipe `vozidlo_vybava`.

export const VOZIDLO_VYBAVA_FORM_ID = 'vozidlo-vybava-form';

/** One row of the vehicle's `vozidlo_vybava` editor (flat list). */
export interface VozidloEquipRow {
  doekod: string;
  nazev: string;
  cena: string;
  cenaSDph: string;
  id_dph: number;
}

const emptyEquipRow = (): VozidloEquipRow => ({
  doekod: '',
  nazev: '',
  cena: '0',
  cenaSDph: '0.00',
  id_dph: 0,
});

/** Loose shape the vehicle equipment endpoint satisfies. */
export interface VozidloEquipmentItemLike {
  doekod?: string | null;
  nazev: string;
  cena?: string | null;
  id_dph?: number | null;
}

type VybavaVariant = 'form' | 'modal';

export function VozidloVybavaFormBody({
  variant,
  initial,
  busy,
  onSubmit,
  onRowsChange,
}: {
  /** `form` = create-continuation surface widths/empty-state; `modal` = dialog. */
  variant: VybavaVariant;
  /** Fresh fetch of the stored rows — re-seeds until the user edits. */
  initial: VozidloEquipmentItemLike[] | undefined;
  busy: boolean;
  /** Modal mode: cleaned replace-all payload (blank-name rows dropped). */
  onSubmit?: (body: { equipment: VehicleEquipmentInput[] }) => void;
  /** Stacked-form mode: mirrors seed + every edit up (rows still carry mirrors). */
  onRowsChange?: (rows: VozidloEquipRow[]) => void;
}) {
  const vatRates = useVatRatesQuery();

  const [rows, setRows] = useState<VozidloEquipRow[]>([]);
  // Re-seed from the server data on every fetch until the user starts editing
  // (see `section-forms` EquipmentFormBody for why a one-time seed is wrong).
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!initial || dirty) return;
    const rates = vatRates.data?.rates ?? [];
    const seeded = initial.map((it) => ({
      doekod: it.doekod ?? '',
      nazev: it.nazev,
      cena: String(it.cena ?? '0'),
      cenaSDph: sDphFromBez(String(it.cena ?? '0'), it.id_dph ?? 0, rates),
      id_dph: it.id_dph ?? 0,
    }));
    setRows(seeded);
    onRowsChange?.(seeded);
    // `rates` only affects the derived s-DPH mirror, so re-seeding when it
    // lands is correct; `onRowsChange` is a stable-enough mirror callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, dirty, vatRates.data]);

  const change = (next: VozidloEquipRow[]) => {
    setRows(next);
    setDirty(true);
    onRowsChange?.(next);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.({ equipment: cleanVozidloEquipment(rows) });
  };

  return (
    <form id={VOZIDLO_VYBAVA_FORM_ID} onSubmit={submit}>
      <VozidloEquipmentRows
        rows={rows}
        vatRates={vatRates.data?.rates ?? []}
        onChange={change}
        variant={variant}
      />
    </form>
  );
}

/**
 * Map editor rows to the replace-all PUT payload: blank-name rows dropped
 * (legacy server-side behavior) and the UI-only `cenaSDph` mirror stripped —
 * only `cena` (bez DPH) is persisted.
 */
export function cleanVozidloEquipment(rows: VozidloEquipRow[]): VehicleEquipmentInput[] {
  return rows
    .filter((r) => r.nazev.trim().length > 0)
    .map((r) => ({
      doekod: r.doekod,
      nazev: r.nazev,
      cena: r.cena,
      id_dph: r.id_dph || undefined,
    }));
}

/**
 * Inline editor for the vehicle's flat equipment list. Mirrors the
 * nabídka/smlouva "Originální výbava" table (code + name + price + VAT,
 * add/remove rows) but the `vozidlo_vybava` set is flat, so there's no
 * original/neoriginal split.
 */
function VozidloEquipmentRows({
  rows,
  vatRates,
  onChange,
  variant,
}: {
  rows: VozidloEquipRow[];
  vatRates: VatRate[];
  onChange: (rows: VozidloEquipRow[]) => void;
  variant: VybavaVariant;
}) {
  const update = (i: number, patch: Partial<VozidloEquipRow>) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, emptyEquipRow()]);

  return (
    <div className="space-y-3">
      {rows.length === 0 &&
        (variant === 'form' ? (
          <EmptyState
            eyebrow="Beze záznamů"
            title="Zatím žádná výbava"
            description='Použijte tlačítko "Přidat položku" pro vložení prvního řádku.'
          />
        ) : (
          <p className="text-[12px] italic text-ink-3">Zatím žádná výbava.</p>
        ))}
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[80px_1fr_130px_130px_150px_36px] items-end gap-2 border border-line p-3 max-sm:grid-cols-2"
          aria-label={`Položka výbavy ${i + 1}`}
        >
          <Field label="Kód">
            <TextInput
              value={r.doekod}
              onChange={(e) => update(i, { doekod: e.currentTarget.value })}
            />
          </Field>
          <Field label="Název">
            <TextInput
              value={r.nazev}
              onChange={(e) => update(i, { nazev: e.currentTarget.value })}
            />
          </Field>
          <EquipmentPriceFields value={r} vatRates={vatRates} onChange={(next) => update(i, next)} />
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="close" size={12} />}
            aria-label="Odebrat položku"
            onClick={() => remove(i)}
          />
        </div>
      ))}
      <Button variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={add}>
        Přidat položku
      </Button>
    </div>
  );
}
